/** @jsxImportSource @emotion/react */

import { faUpRightFromSquare } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useParams } from "react-router-dom";
import { getFilledArrayOrDefault } from "../../../../utils";
import { FromLink } from "../../../../utils/useNavigateFrom";
import { MetaConditionsForm } from "./ColorCodeTriggerForm";
import FilterableConfigList from "./FilterableConfigList";
import { attributeIsSet } from "./flagConstants";


const defaultField = {
    field: {
        gte: false,
        lt: 16,
        label: "Overripe",
        color: "#FFA500"
    }
};

// TODO: This component is almost the same as FlagConfigForm. Could probably be merged into one component with a prop to determine if it's a flag or category config form.
export default function CategoryConfigForm({ name, title, fields, description, setArray, useDisplayFilter = false }) {
    const { tenant_id } = useParams();

    const getMetaTriggerTitle = (object) => {
        const link = <FromLink to={`/tenants/${tenant_id}/form-field-options/${object.options_name}`} target="_blank" rel="noopener noreferrer" >{object.meta_location} [{object.options_name}]<FontAwesomeIcon icon={faUpRightFromSquare} /> </FromLink>;
        // Generate string for included values
        const includeValuesString = object.include_values && object.include_values.length > 0
            ? `incl: ${object.include_values.join(", ")}`
            : "";
            // Generate string for excluded values
        const excludeValuesString = object.exclude_values && object.exclude_values.length > 0
            ? `excl: ${object.exclude_values.join(", ")}`
            : "";
            // Combine the options name with include and exclude values strings, separating with a space when necessary
        const titleParts = [includeValuesString, excludeValuesString].filter((part) => part !== "");
        return <span><span className="ms-3 me-2">{link}</span>{titleParts.join(" - ")}</span>;
    };

    const getFieldTitle = (object) => {
        const conditions: string[] = [];

        // Check and add the conditions based on the object's properties
        if (attributeIsSet(object.gte)) {
            conditions.push(`>= ${object.gte}`);
        }
        if (attributeIsSet(object.gt)) {
            conditions.push(`> ${object.gt}`);
        }
        if (attributeIsSet(object.lte)) {
            conditions.push(`<= ${object.lte}`);
        }
        if (attributeIsSet(object.lt)) {
            conditions.push(`< ${object.lt}`);
        }

        if (attributeIsSet(object.eq)) {
            conditions.push(`= ${object.eq}`);
        }

        const triggers = getFilledArrayOrDefault(object?.meta_conditions, []).map(getMetaTriggerTitle);


        // Join the conditions with a comma and space for readability
        const conditionsString = conditions.join(", ");

        // Return the formatted string
        return <span><b>{object.label}</b> [{object.value}] {conditionsString} {triggers}</span>;
    };

    const getFieldForm = (object, setObject) => [
        {
            label: "Greater than or equal to",
            name: "gte",
            type: "number",
        },
        {
            label: "Less than",
            name: "lt",
            type: "number",
        },
        {
            label: "Greater than",
            name: "gt",
            type: "number",
        },
        {
            label: "Less than or equal to",
            name: "lte",
            type: "number",
        },
        {
            label: "Equals",
            name: "eq",
            type: "text",
            description: "Use this field to compare string values"
        },
        {
            label: "Value",
            name: "value",
            type: "text",
            description: "A value for this category. to store in DB. e.g. `rte` or `overripe`.",
        },
        {
            label: "Label",
            name: "label",
            type: "text",
        },
        {
            label: "Color",
            name: "color",
            type: "text",
            description: "Color for the category in hex format. For example #FFA500. Used in some components."
        },
        {
            label: "External code (SAP CODE1)",
            name: "external_code",
            type: "text",
            description: "Optional explicit code sent to the external-status (Bakker SAP) integration for this bucket (INSPCHAR=0010 CODE1). Takes precedence over the per-fruit default vocabulary when set. E.g. use this to map a banana `target` bucket to the numeric color \"3\".",
        },
        {
            label: "Class",
            name: "className",
            type: "text",
            description: "A class to give to your button. Use `text-white` if your color is really dark."
        },
        {
            type: "header",
            label: "Triggers conditions",
        },
        {
            type: "element",
            name: "meta_conditions",
            el: <MetaConditionsForm name="meta_conditions" title="Meta conditions" description="Define meta conditions to filter on" fields={object?.meta_conditions || []} setArray={(meta_conditions) => setObject({ ...object, meta_conditions })} />
        }
    ];
    return <FilterableConfigList
        useDisplayFilter={useDisplayFilter as false | string[]}
        name={name}
        title={title}
        description={description}
        fields={fields}
        setArray={setArray}
        defaultField={defaultField}
        getFieldForm={getFieldForm}
        getFieldTitle={getFieldTitle} />;

}
